// /quotes — 报价列表
// v1.9 P0: 移动端表格退化为 card list
// v1.27.2 fix: 加 <Outlet /> 渲染 /quotes/$quoteId 和 /quotes/new 子路由
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { EmptyState } from '../components/EmptyState';
import { CardListSkeleton, TableSkeleton } from '../components/Skeleton';
import { type Quote, quoteApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/quotes')({
  component: QuotesPage,
});

const STATUS_LABELS: Record<Quote['status'], string> = {
  draft: '草稿',
  sent: '已发',
  accepted: '已接受',
  rejected: '已拒绝',
  expired: '已过期',
};

const STATUS_COLORS: Record<Quote['status'], string> = {
  draft: 'tag',
  sent: 'tag tag-stage-quoted',
  accepted: 'tag tag-good',
  rejected: 'tag',
  expired: 'tag',
};

function fmtCents(cents: number, cur: string) {
  const amount = cents / 100;
  if (cur === 'JPY') return `¥${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${cur === 'USD' ? '$' : cur === 'EUR' ? '€' : cur} ${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function QuotesPage() {
  const navigate = useNavigate();
  const { currentCompanyId } = useAuthStore();
  // v1.27.2 fix: 详情/新建时只渲染 Outlet, 不渲染 list
  const location = useLocation();
  const onChild =
    location.pathname !== '/quotes' &&
    (location.pathname.startsWith('/quotes/') || location.pathname === '/quotes/new');
  const { data, isLoading } = useQuery({
    queryKey: ['quotes', currentCompanyId],
    queryFn: () => quoteApi.list(),
    enabled: !onChild,
  });

  if (onChild) return <Outlet />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold mb-1">报价</h1>
          <p className="text-ink-3 text-xs sm:text-sm">共 {data?.total ?? 0} 份 · 当前公司视角</p>
        </div>
        <button
          type="button"
          onClick={() => navigate({ to: '/quotes/new' })}
          className="w-full sm:w-auto px-4 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-accent-2"
        >
          + 创建报价
        </button>
      </div>

      <div className="bg-surface border border-line rounded overflow-hidden">
        {isLoading ? (
          <>
            <div className="hidden md:block">
              <TableSkeleton rows={5} />
            </div>
            <div className="md:hidden p-2">
              <CardListSkeleton rows={5} />
            </div>
          </>
        ) : data?.items.length === 0 ? (
          <EmptyState
            icon="diamond"
            title="还没有报价"
            description="在询盘详情里点「报价」, 或手动新建 PI 算价"
          />
        ) : (
          <>
            {/* 桌面: 表格 */}
            <table className="hidden md:table w-full">
              <thead>
                <tr className="bg-surface-2 text-ink-2 text-xs">
                  <th className="text-left px-4 py-2 font-medium">编号</th>
                  <th className="text-left px-4 py-2 font-medium">Incoterm</th>
                  <th className="text-left px-4 py-2 font-medium">金额</th>
                  <th className="text-left px-4 py-2 font-medium">状态</th>
                  <th className="text-left px-4 py-2 font-medium">有效期</th>
                  <th className="text-left px-4 py-2 font-medium">创建时间</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((q) => (
                  <tr key={q.id} className="border-t border-line hover:bg-surface-2">
                    <td className="px-4 py-3">
                      <Link
                        to="/quotes/$quoteId"
                        params={{ quoteId: q.id }}
                        className="font-medium text-sm text-accent hover:underline"
                      >
                        {q.quoteNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {q.incoterm} {q.incotermPlace.split(' ').slice(1).join(' ')}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">
                      {fmtCents(q.selectedTotalCents, q.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={STATUS_COLORS[q.status]}>{STATUS_LABELS[q.status]}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-3">
                      {new Date(q.validUntil).toLocaleDateString('zh-CN')}
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-3">
                      {new Date(q.createdAt).toLocaleDateString('zh-CN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 移动: 卡片列表 */}
            <ul className="md:hidden p-2 space-y-2">
              {data?.items.map((q) => (
                <li
                  key={q.id}
                  className="bg-surface-2 border border-line rounded-lg p-3 animate-fade-up"
                >
                  <Link to="/quotes/$quoteId" params={{ quoteId: q.id }} className="block">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm text-accent">{q.quoteNumber}</div>
                        <div className="text-xs text-ink-2 mt-0.5">
                          {q.incoterm} {q.incotermPlace.split(' ').slice(1).join(' ')}
                        </div>
                      </div>
                      <span className={`${STATUS_COLORS[q.status]} shrink-0`}>
                        {STATUS_LABELS[q.status]}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-line">
                      <span className="text-base font-semibold text-ink">
                        {fmtCents(q.selectedTotalCents, q.currency)}
                      </span>
                      <span className="text-xs text-ink-3">
                        有效 {new Date(q.validUntil).toLocaleDateString('zh-CN')}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
